import { useApiClient } from '@/api/ApiClientProvider'
import { isNotLiveMethod, type NotLiveMethod } from '@/api/HttpApiClient'
import type { DataMeta } from '@/api/types'
import { Card, EmptyState } from '@/design'
import { usePageFreshness } from '@/lib/freshness'

import type { SectionKey } from './routes'

const NOT_LIVE_META: DataMeta = { source: 'not_live', lastUpdatedAt: null }

/**
 * Whether this client answers `method` as not live (docs/architecture/web-not-live-sections.md).
 * Known on the first render, before any request resolves, so a screen can
 * withhold a dataset and every control that would change it without ever
 * flashing them: the one check a screen makes, not twenty ad-hoc ones.
 */
export function useNotLive(method: NotLiveMethod): boolean {
  return isNotLiveMethod(useApiClient(), method)
}

/**
 * `useNotLive` for a whole page, and the page's one freshness declaration:
 * "Not live yet" while its section is not live, otherwise `liveMeta` — the
 * page's own answer, once it has one (omit it for a page with no provenance
 * of its own). Call it unconditionally at the top of the page, before any
 * early return, and do not also call `usePageFreshness`.
 */
export function useNotLivePage(method: NotLiveMethod, liveMeta?: DataMeta): boolean {
  const notLive = useNotLive(method)
  usePageFreshness(notLive ? NOT_LIVE_META : liveMeta)
  return notLive
}

/** The full-size state a not-live page shows in place of all of its content and controls. */
export function NotLivePage() {
  return (
    <Card>
      <EmptyState variant="not-live" />
    </Card>
  )
}

/**
 * The navigation sections with no live page at all, each by the method its
 * pages depend on. Sections with at least one real page (Overview, Insights'
 * alerts, Analytics, Marketing) are not listed; their not-live pages say so
 * one by one. A section drops out of the tags when its method leaves the
 * delegation list (this map then stops compiling).
 */
export const NOT_LIVE_SECTION_METHODS: Partial<Record<SectionKey, NotLiveMethod>> = {
  automation: 'workflows',
  competitions: 'listings',
  market: 'marketCompetitors',
  reports: 'savedReports',
}
